import { FindingsAlert, FindingsSelector } from '@app/icare/components/findings'
import { PatientCareManagerEditDrawer } from '@app/icare/entities/patient/edits/PatientCareManagerEditDrawer'
import {
  patientFindingsQueryOptions,
  selectByGroup,
} from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import {
  type PatientCareManagerRelation,
  PatientCareManagerRole,
} from '@app/icare/entities/patient/schema/PatientCareManager'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { UserConfig } from '@app/icare/entities/user/config'
import { patientCareManagerQueries } from '@app/icare/entities/user/queries/patientCareManagerQueries'
import { isActivePatientRelation } from '@app/icare/shared/api'
import { t } from '@lingui/core/macro'
import { entityParams } from '@pkg/db/utils'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Flex, Table } from 'antd'
import dayjs from 'dayjs'
import { FaPencil } from 'react-icons/fa6'
import { useRelationCommitNavigation } from '../-components/useRelationCommitNavigation'

export const Route = createFileRoute('/_app/patienten/$id/_edit/zustandigkeit/versorgungsmanager')({
  params: entityParams({ id: PatientId }),
  loader: ({ params, context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(patientCareManagerQueries.forList(params.id)),
      queryClient.ensureQueryData(patientFindingsQueryOptions(params.id)),
    ]),
  component: RouteComponent,
})

function RouteComponent() {
  const { id: patientId } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: managers = [] } = useSuspenseQuery(patientCareManagerQueries.forList(patientId))

  const handleAfterCommitCareManager = useRelationCommitNavigation<PatientCareManagerRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: _relation =>
      navigate({
        to: '/patienten/$id/zustandigkeit/versorgungsmanager',
        params: { id: patientId },
      }),
    onInactiveEditNavigate: () =>
      navigate({ to: '/patienten/$id/zustandigkeit/versorgungsmanager', params: { id: patientId } }),
  })
  return (
    <Flex vertical gap="small">
      <FindingsSelector selector={selectByGroup('careManagers')} children={f => <FindingsAlert findings={f} />} />

      <Table
        dataSource={managers}
        rowKey="id"
        locale={{ emptyText: t`Keine Versorgungsmanager vorhanden` }}
        size="small"
        columns={[
          { title: t`Name`, render: (_, r) => UserConfig.format.primary(r.to) },
          {
            title: t`Rolle`,
            render: (_, r) => (r.role === PatientCareManagerRole.primary ? t`Primär` : t`Temporär`),
          },
          { title: t`E-Mail`, render: (_, r) => r.to?.email },
          { title: t`Telefon`, render: (_, r) => r.to?.phone },
          {
            title: t`Zuständig von`,
            render: (_, r) => (r.activeFrom ? dayjs(r.activeFrom).format('DD.MM.YYYY') : '-'),
          },
          { title: t`Zuständig bis`, render: (_, r) => (r.activeTo ? dayjs(r.activeTo).format('DD.MM.YYYY') : '-') },
          {
            key: 'actions',
            width: 80,
            render: (_, r) => (
              <Button
                type="text"
                size="small"
                icon={<FaPencil />}
                onClick={() =>
                  void PatientCareManagerEditDrawer.show({
                    patientId,
                    relationId: r.id,
                    section: 'period',
                    onAfterCommit: handleAfterCommitCareManager,
                  })
                }
              />
            ),
          },
        ]}
      />
    </Flex>
  )
}
